import dynamic from 'next/dynamic';
import htmlParser from 'html-react-parser';
import { useTheme } from 'styled-components';

import cmsParser from 'Common/util/cmsParser';
import { h4AsBilo } from 'Common/util/parserOptions';

import type { PageEntry, Node } from 'contensis-delivery-api';
import type { Props } from './Professional';

export type PageData = {
    entry: PageEntry;
    ancestors: Node[];
};

const datamap = (data: PageData): Props => {
    const { entry, ancestors } = data;
    const { heroImage, name } = entry;

    const { colors } = useTheme();

    return {
        breadcrumbs: cmsParser.breadcrumbs(ancestors),
        heroBanner: {
            image: cmsParser.image(heroImage, '/cms'),
            heading: name,
            applyButton: {
                label: 'How to apply',
                ariaLabel: 'Find out more about how to apply',
                href: '#apply'
            }
        },
        courseInformation: {
            institutionCode: 'S30',
            duration: {
                label: 'Duration',
                text: entry.duration
            },
            delivery: {
                label: 'Delivery',
                text: entry.delivery
            }
        },
        introduction: entry.introduction,
        overview: entry.overview.map(item => {
            switch (item.type) {
                case 'text':
                    return { text: htmlParser(item.value) };
                case 'videoTranscript':
                    return { video: cmsParser.transcriptEmbed(item.value) };
                default:
                    return {};
            }
        }),
        facts:
            entry.facts.length > 0
                ? {
                      id: 'course-facts',
                      label: 'Course facts',
                      items: entry.facts.map(fact => {
                          switch (fact.sys.contentTypeId) {
                              case 'accreditation':
                                  return {
                                      id: fact.sys.id,
                                      type: 'accreditation',
                                      accreditation: {
                                          text: fact.name,
                                          image: cmsParser.image(fact.image)
                                      }
                                  };
                              case 'courseStat':
                                  return {
                                      id: fact.sys.id,
                                      type: 'stat',
                                      stat: {
                                          stat: fact.stat.stat,
                                          text: fact.stat.description
                                      }
                                  };
                          }
                      })
                  }
                : undefined,
        sellingPoints: entry.sellingPoints.map(point => ({
            heading: point.heading,
            id: point.sys.id,
            icon: cmsParser.image(point.icon),
            variant: 'white',
            children: <p>{point.description}</p>,
            button: {
                ariaLabel: point.ariaLabel || 'Open'
            }
        })),
        whatWillLearn: {
            heading: entry.whatWillILearnHeading,
            text: htmlParser(entry.whatWilliLearnText)
        },
        leadsTo: {
            heading: entry.leadsToHeading,
            text: htmlParser(entry.leadsToText)
        },
        learningAssessment: {
            heading: "How you'll learn and be assessed",
            learnBy:
                entry.learningTypes.length > 0
                    ? {
                          heading: entry.learningSubheading,
                          list: {
                              items: entry.learningTypes.map(item => ({
                                  text: item.label,
                                  icon: item.icon
                                      ? {
                                            as: dynamic(() =>
                                                import('@solent-university/solent-icons').then(
                                                    icons => icons[item.icon.name]
                                                )
                                            )
                                        }
                                      : { src: cmsParser.image(item.image).src },
                                  iconColour: colors.accents[3].hex,
                                  id: item.sys.id
                              }))
                          }
                      }
                    : undefined,
            assessedBy:
                entry.assessmentTypes.length > 0
                    ? {
                          heading: entry.assessmentSubheading,
                          list: {
                              items: entry.assessmentTypes.map(item => ({
                                  text: item.label,
                                  icon: item.icon
                                      ? {
                                            as: dynamic(() =>
                                                import('@solent-university/solent-icons').then(
                                                    icons => icons[item.icon.name]
                                                )
                                            )
                                        }
                                      : { src: cmsParser.image(item.image).src },
                                  iconColour: colors.accents[3].hex,
                                  id: item.sys.id
                              }))
                          }
                      }
                    : undefined,
            text: htmlParser(entry.learnAssessText.text),
            image: cmsParser.image(entry.learnAssessText.image),
            link: { ...cmsParser.link(entry.learnAssessLink), variant: 'threeDark' }
        },
        tuition: {
            subHeading: entry.feesSubheading,
            text: htmlParser(entry.feesText)
        },
        extraCosts: entry.otherCosts ? htmlParser(entry.otherCosts) : undefined,
        financialSupport: {
            heading: 'Bursaries, scholarships and financial support',
            children: htmlParser(entry.financialSupport.text)
        },
        howToApply: htmlParser(entry.entryRequirements, h4AsBilo),
        courseApply:
            entry.applyLink.length > 0
                ? {
                      apply: {
                          heading: 'Make your application'
                      },
                      openDay: {
                          heading: 'Not ready to apply?',
                          link: cmsParser.linkButton(entry.openDayLink)
                      },
                      select: {
                          id: 'course-apply',
                          defaultValue: entry.applyLink[0].link.sys.id,
                          name: 'course-apply',
                          options: [
                              ...entry.applyLink.map(item => ({
                                  value: item.link.sys.id,
                                  label: item.label,
                                  link: cmsParser.link(item),
                                  id: item.link.sys.id
                              }))
                          ]
                      }
                  }
                : undefined,
        relatedCourses: {
            cards: entry.relatedCoursesLink.map(item => ({
                id: item.sys.id,
                image: cmsParser.image(item.courseCardImage),
                heading: item.name,
                description: item.courseDescription,
                link: {
                    href: item.sys.uri,
                    label: 'Find out more',
                    ariaLabel: `Find out more ${item.title}`
                }
            }))
        }
    };
};

export default datamap;
